import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Clock, LogOut, Loader2, HardHat, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = window.location.origin;

const STATUS_MAP = {
  signe: { label: 'Signe', cls: 'bg-blue-500' },
  travaux_en_cours: { label: 'Travaux en cours', cls: 'bg-orange-500' },
  complete: { label: 'Complete', cls: 'bg-green-500' },
  annule: { label: 'Annule', cls: 'bg-red-500' },
};

// P-FLOAT — Mirrors the GPS capture used by MobileWorksite. Resolves to
// null when the browser denies / times out so the punch can still be
// recorded without coords (the admin will see "GPS: non capté").
function getGeoLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
}

export default function MobileHome() {
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [worksites, setWorksites] = useState([]);
  // P-FLOAT — Currently open floating punch (or null). Mirrored from the
  // /worksites endpoint so we don't need a second round-trip.
  const [floatingActivity, setFloatingActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [floatingBusy, setFloatingBusy] = useState(false);

  const token = localStorage.getItem('emp_token');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/mobile/worksites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { if (res.status === 401) { logout(); return; } throw new Error(); }
      const data = await res.json();
      setWorksites(data.worksites || []);
      setFloatingActivity(data.floating_activity || null);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); setRefreshing(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) { navigate('/mobile'); return; }
    const data = localStorage.getItem('emp_data');
    if (data) setEmp(JSON.parse(data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await fetch(`${API}/api/mobile/auth/logout`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    localStorage.removeItem('emp_token');
    localStorage.removeItem('emp_data');
    navigate('/mobile');
  };

  const refresh = () => { setRefreshing(true); load(); };

  // -------------------------------------------------------------------------
  // P-FLOAT — Floating punch actions executed inline from this page.
  // We deliberately do NOT push a new route; floating punches are a one-tap
  // action that closes from this same screen via the "Pointer depart" CTA.
  // -------------------------------------------------------------------------
  const floatingClockIn = async () => {
    if (floatingBusy) return;
    setFloatingBusy(true);
    try {
      const geo = await getGeoLocation();
      const res = await fetch(`${API}/api/mobile/clock-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...(geo || {}) }),  // no client_id → floating
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        toast.error(detail.detail || 'Erreur pointage');
        return;
      }
      toast.success('Pointage flottant enregistre');
      await load();
    } finally { setFloatingBusy(false); }
  };

  const floatingClockOut = async () => {
    if (floatingBusy || !floatingActivity) return;
    setFloatingBusy(true);
    try {
      const geo = await getGeoLocation();
      const res = await fetch(`${API}/api/mobile/clock-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activity_id: floatingActivity.activity_id, ...(geo || {}) }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        toast.error(detail.detail || 'Erreur depart');
        return;
      }
      toast.success('Depart enregistre');
      await load();
    } finally { setFloatingBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  // ---- Derived state shared by all worksite cards and the floating card. --
  const activeSiteId = worksites.find(ws => ws.today_activity && !ws.today_activity.clock_out)?.client_id || null;
  const isFloatingOpen = !!floatingActivity;
  // Bidirectional lock: a worksite punch blocks the floating button, a
  // floating punch blocks ALL worksite buttons. The global server-side
  // lock in /clock-in is the source of truth; this UI guard avoids a
  // useless round-trip + makes the conflict immediately readable.
  const floatingBlocked = !!activeSiteId;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mobile-home">
      {/* Header */}
      <div className="bg-orange-500 text-white px-4 py-4 sticky top-0 z-10 safe-area-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardHat className="h-6 w-6" />
            <div>
              <p className="font-bold text-sm leading-tight">{emp?.first_name} {emp?.last_name}</p>
              <p className="text-orange-100 text-xs">Mes chantiers</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={refresh} className="text-white hover:bg-orange-600 h-9 w-9">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={logout} className="text-white hover:bg-orange-600 h-9 w-9" data-testid="mobile-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3 pb-20">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
          {new Date().toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
          {' '}&middot;{' '}{worksites.length} chantier{worksites.length !== 1 ? 's' : ''}
        </p>

        {worksites.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <HardHat className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">Aucun chantier assigne aujourd&apos;hui</p>
            <p className="text-xs mt-1">Vous pouvez quand même pointer ci-dessous</p>
          </div>
        )}

        {worksites.map(ws => {
          const st = STATUS_MAP[ws.status] || STATUS_MAP.signe;
          const activity = ws.today_activity;
          const isClockedIn = activity && !activity.clock_out;
          // P-FLOAT — extended block: an OPEN punch elsewhere (other site
          // OR floating) disables this site's button.
          const isBlockedByOther =
            (activeSiteId && !isClockedIn && activeSiteId !== ws.client_id) ||
            (isFloatingOpen && !isClockedIn);
          const blockedReason = isFloatingOpen
            ? "Terminez le pointage sans chantier d'abord"
            : "Terminez le chantier en cours d'abord";
          return (
            <Card
              key={ws.client_id}
              className={`active:scale-[0.98] transition-transform ${isClockedIn ? 'ring-2 ring-orange-400' : ''}`}
              data-testid={`mobile-ws-${ws.client_id}`}
            >
              <CardContent className="p-5 space-y-4">
                {/* Client name + status */}
                <div className="flex items-start justify-between">
                  <h3 className="font-bold text-lg leading-tight">{ws.first_name} {ws.last_name}</h3>
                  <Badge className={`${st.cls} text-white text-xs shrink-0 ml-2`}>{st.label}</Badge>
                </div>

                {/* Address - clickable to Google Maps */}
                {(ws.address || ws.city) && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent([ws.address, ws.city].filter(Boolean).join(', '))}`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-lg px-3 py-2.5"
                  >
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span className="underline">{[ws.address, ws.city].filter(Boolean).join(', ')}</span>
                  </a>
                )}

                {/* Clock status */}
                {isClockedIn && (
                  <div className="bg-orange-50 rounded-lg px-3 py-2.5 space-y-1">
                    <div className="flex items-center gap-2 text-sm text-orange-700 font-semibold">
                      <Clock className="h-4 w-4" />
                      Pointe depuis {new Date(activity.clock_in).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {activity.clock_in_location && (
                      <div className="flex items-center gap-1 text-xs text-orange-500" data-testid={`gps-in-${ws.client_id}`}>
                        <MapPin className="h-3 w-3" />
                        GPS: {activity.clock_in_location.lat.toFixed(4)}, {activity.clock_in_location.lng.toFixed(4)}
                      </div>
                    )}
                  </div>
                )}
                {activity?.clock_out && (
                  <div className="bg-green-50 rounded-lg px-3 py-2.5 space-y-1">
                    <div className="flex items-center gap-2 text-sm text-green-700 font-semibold">
                      <Clock className="h-4 w-4" />
                      {new Date(activity.clock_in).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                      {' - '}
                      {new Date(activity.clock_out).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {activity.clock_in_location && (
                      <div className="flex items-center gap-1 text-xs text-green-500" data-testid={`gps-done-${ws.client_id}`}>
                        <MapPin className="h-3 w-3" />
                        GPS: {activity.clock_in_location.lat.toFixed(4)}, {activity.clock_in_location.lng.toFixed(4)}
                      </div>
                    )}
                  </div>
                )}

                {/* Action button */}
                {isBlockedByOther ? (
                  <div className="space-y-2">
                    <Button disabled className="w-full h-12 text-base font-bold rounded-xl bg-gray-300 cursor-not-allowed" data-testid={`punch-btn-${ws.client_id}`}>
                      <Clock className="mr-2 h-5 w-5" />
                      Pointer arrivee
                    </Button>
                    <p className="text-xs text-center text-orange-600 font-medium">{blockedReason}</p>
                  </div>
                ) : (
                  <Button
                    onClick={() => navigate(`/mobile/worksite/${ws.client_id}`)}
                    className={`w-full h-12 text-base font-bold rounded-xl ${
                      isClockedIn
                        ? 'bg-red-500 hover:bg-red-600'
                        : activity?.clock_out
                          ? 'bg-gray-400 hover:bg-gray-500'
                          : 'bg-green-500 hover:bg-green-600'
                    }`}
                    data-testid={`punch-btn-${ws.client_id}`}
                  >
                    <Clock className="mr-2 h-5 w-5" />
                    {isClockedIn ? 'Pointer depart' : activity?.clock_out ? 'Voir le chantier' : 'Pointer arrivee'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* P-FLOAT — Always-visible floating-punch card.
            Renders BELOW the worksites grid so the daily worksites stay at
            the top of the screen. Bidirectional lock: greyed-out when a
            worksite punch is currently open. */}
        <div className="pt-2">
          <div className="flex items-center gap-3 my-3" data-testid="floating-divider">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">ou</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          <Card
            className={`active:scale-[0.98] transition-transform ${isFloatingOpen ? 'ring-2 ring-purple-400' : ''}`}
            data-testid="mobile-floating-card"
          >
            <CardContent className="p-5 space-y-3">
              <div>
                <h3 className="font-bold text-base leading-tight">Pointer sans chantier</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {isFloatingOpen
                    ? 'Pointage en cours, sans chantier rattaché. Votre administrateur pourra l\'associer plus tard.'
                    : 'Utilisez cette option si vous travaillez aujourd\'hui hors d\'un chantier listé. L\'admin pourra l\'attacher plus tard.'}
                </p>
              </div>

              {isFloatingOpen && (
                <div className="bg-purple-50 rounded-lg px-3 py-2.5 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-purple-700 font-semibold">
                    <Clock className="h-4 w-4" />
                    Pointe depuis {new Date(floatingActivity.clock_in).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {floatingActivity.date && floatingActivity.date !== new Date().toISOString().slice(0, 10) && (
                    <div
                      className="mt-2 text-xs bg-amber-100 border border-amber-300 text-amber-900 rounded px-2 py-1.5"
                      data-testid="floating-stale-banner"
                    >
                      ⚠️ Pointage ouvert depuis le <strong>{floatingActivity.date}</strong>.
                      Pointez votre depart pour fermer ce pointage. L&apos;heure de sortie
                      sera enregistree maintenant.
                    </div>
                  )}
                  {floatingActivity.clock_in_location && (
                    <div className="flex items-center gap-1 text-xs text-purple-500" data-testid="gps-floating-in">
                      <MapPin className="h-3 w-3" />
                      GPS: {floatingActivity.clock_in_location.lat.toFixed(4)}, {floatingActivity.clock_in_location.lng.toFixed(4)}
                    </div>
                  )}
                </div>
              )}

              {floatingBlocked ? (
                <div className="space-y-2">
                  <Button disabled className="w-full h-12 text-base font-bold rounded-xl bg-gray-300 cursor-not-allowed" data-testid="floating-btn">
                    <Clock className="mr-2 h-5 w-5" />
                    Pointer sans chantier
                  </Button>
                  <p className="text-xs text-center text-orange-600 font-medium">Terminez le chantier en cours d&apos;abord</p>
                </div>
              ) : isFloatingOpen ? (
                <Button
                  onClick={floatingClockOut}
                  disabled={floatingBusy}
                  className="w-full h-12 text-base font-bold rounded-xl bg-red-500 hover:bg-red-600"
                  data-testid="floating-btn"
                >
                  {floatingBusy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Clock className="mr-2 h-5 w-5" />}
                  Pointer depart
                </Button>
              ) : (
                <Button
                  onClick={floatingClockIn}
                  disabled={floatingBusy}
                  className="w-full h-12 text-base font-bold rounded-xl bg-purple-600 hover:bg-purple-700"
                  data-testid="floating-btn"
                >
                  {floatingBusy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Clock className="mr-2 h-5 w-5" />}
                  Pointer arrivee (sans chantier)
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
